import {
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
  Dispatch as ReactDispatch,
} from 'react';
import { AnyAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import SubmittedProvider from '@paradigm/features/src/forms/submitted-context';
import { selectActiveProducts } from '@paradigm/store/auth';
import { useStoredEntry } from '#/store/useStoredEntry';

import * as store from '#/unified-rfqs/ui/rfq-builder/store';
import {
  ActionsCtx,
  useCreateActions,
} from '#/unified-rfqs/ui/rfq-builder/actions';
import { createView, ViewCtx } from '#/unified-rfqs/ui/rfq-builder/view';
import usePrefillNotifier from '#/unified-rfqs/ui/rfq-builder/usePrefillNotifier';
import { useMultiProductVenues } from '#/feature-flags/context';
import usePremiumAdjustedDeltaHedge from '#/unified-rfqs/ui/rfq-builder/usePremiumAdjustedDeltaHedge';
import {
  useDrfqInstruments,
  useGrfqInstruments,
  useInstrumentsFetcher,
} from '#/unified-rfqs/ui/instruments/context';
import { getDefaultProductCode } from '#/products/domain';
import {
  getDefaultVenueCode,
  Source,
} from '#/unified-rfqs/entities/products/venue';
import useHedgeLegPricePrefill from './useHedgeLegPricePrefill';

interface Props {
  readonly children: ReactNode;
  readonly prefill?: Partial<store.State>;
  readonly onPrefillError: (message: string) => void;
}

export function RfqBuilderProvider({
  children,
  prefill = {},
  onPrefillError,
}: Props) {
  const anonymousStored = useAnonymousStoredState();
  const defaultVenue = useDefaultVenue();
  const { state, dispatch } = useRfqBuilderStore(
    anonymousStored,
    prefill,
    defaultVenue,
  );
  usePrefillNotifier({
    onError: onPrefillError,
    instruments: state.instruments,
    prefill,
  });

  useEffect(() => {
    anonymousStored.set(state.anonymous);
  }, [anonymousStored, state.anonymous]);

  const actions = useCreateActions(dispatch);
  const multiProductVenues = useMultiProductVenues();
  const view = createView(state, { multiProductVenues });

  useHedgeLegPricePrefill(state, actions);
  usePremiumAdjustedDeltaHedge({ state, greeks: view.greeks, dispatch });

  return (
    <SubmittedProvider>
      <ActionsCtx.Provider value={actions}>
        <ViewCtx.Provider value={view}>{children}</ViewCtx.Provider>
      </ActionsCtx.Provider>
    </SubmittedProvider>
  );
}

function useAnonymousStoredState() {
  const drfqAnonymousEntry = useStoredEntry<boolean>('um:drfq:anonymous');

  return useMemo(
    () => ({
      get: () => {
        const defaultValue = true;
        const value = drfqAnonymousEntry.get();
        if (value == null || typeof value !== 'string') return defaultValue;
        return JSON.parse(value) as boolean;
      },
      set: (value: boolean) => drfqAnonymousEntry.set(String(value)),
    }),
    [drfqAnonymousEntry],
  );
}

/**
 * Venue the builder opens on, from the venues the user's firm is entitled
 * to. `INITIAL_STATE` cannot carry it: the selectors filter their options by
 * the same entitlements, so a fixed default renders as a selected venue that
 * is absent from the list the user can pick from.
 *
 * `undefined` when the firm is entitled to no selectable venue, which
 * `VenueSelector` renders as an empty field.
 */
function useDefaultVenue(): Source | undefined {
  const activeProducts = useSelector(selectActiveProducts);
  return useMemo(() => getDefaultVenueCode(activeProducts), [activeProducts]);
}

export function useRfqBuilderStore(
  anonymousStored: ReturnType<typeof useAnonymousStoredState>,
  prefill: Partial<store.State>,
  defaultVenue: Source | undefined,
): { state: store.State; dispatch: ReactDispatch<AnyAction> } {
  const drfqInstruments = useDrfqInstruments();
  const grfqInstruments = useGrfqInstruments();
  // With no entitled venue there is nothing to open on, but the reducer and
  // its views need a venue that exists in `VENUES`, so hold `INITIAL_STATE`'s
  // as a placeholder. `VenueSelector` leaves the field empty in that case
  // instead of naming a venue the firm cannot trade.
  const initialVenue =
    prefill.venue ?? defaultVenue ?? store.INITIAL_STATE.venue;
  const initialAuction = prefill.auction ?? 'RFQ';
  const initialState = {
    ...store.INITIAL_STATE,
    venue: initialVenue,
    // The venue's own default, since `INITIAL_STATE.productCode` belongs to
    // whichever venue `INITIAL_STATE.venue` names.
    productCode: getDefaultProductCode(initialVenue) ?? null,
    anonymous: anonymousStored.get(),
    instruments:
      initialAuction === 'OB'
        ? grfqInstruments[initialVenue]
        : drfqInstruments[initialVenue],
    ...prefill,
  };
  const [state, dispatch] = useReducer(store.reducer, initialState);
  const { auction, venue } = state;
  const { fetch: fetchInstruments } = useInstrumentsFetcher();

  useEffect(() => {
    fetchInstruments(auction ?? 'RFQ', venue, (instruments) =>
      dispatch(store.updateInstruments(instruments)),
    );
    // Should run only auction and venue change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auction, venue]);

  return { state, dispatch };
}
